import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { DEFAULT_PAGE_LOAD_SIZE } from '../../../api/pagination';
import {
    clearClaimQueues,
    searchClaimQueue,
    updateClaimQueueProgress
} from '../../../store/participantService';
import { formatDateRange } from '../../../utils/dateFunctions';
import { getNameFromId } from '../../../utils/directusFunctions';
import { NO_UPDATE_ERROR } from '../../../utils/formValidation/commonErrorConstants';
import {
    CLAIM_EVENT_STATUSES_ERROR,
    CLAIM_EVENT_TYPES_ERROR,
    CLAIMS_ERROR,
    CONTRACT_DETAILS_ERROR
} from '../../../utils/formValidation/loadingErrorMessageConstants';
import { clearKeys, deepEqual, trimEntries } from '../../../utils/objectUtils';
import { stableSort } from '../../../utils/sortFunctions';
import { hasRole, PRAP, QUALITY, SUPERUSER } from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import DateRangePicker from '../../formElements/DateRangePicker';
import SingleSelect from '../../formElements/SingleSelect';
import SearchOnEnter from '../../search/SearchOnEnter';
import ResultsTable from '../../table/ResultsTable';
import LoadingSpinner from '../../ui/LoadingSpinner';
import { validateFilters } from '../validateSearch';

import ClaimTableRow from './ClaimTableRow';
import StatusCannotClaimEditor from './StatusCannotClaimEditor';
import StatusRevertEditor from './StatusRevertEditor';

import app from '../../../app.module.css';
import form from '../../../commonStyles/formStyles.module.css';
import shared from '../claimStyles/claimSharedStyles.module.css';

const headCells = [
    { id: 'ptCode', label: 'ID' },
    { id: 'poNumber', label: 'PO Number' },
    { id: 'eventType', label: 'Event Type', sortable: false },
    { id: 'eventDate', label: 'Event Date' },
    { id: 'lastStatusUpdated', label: 'Last Updated' },
    { id: 'statusName', label: 'Status', sortable: false },
    { id: 'selection', label: 'Proceed / Revert / Cannot Claim / Skip', sortable: false }
];

const initialRowMetaData = {
    order: 'asc',
    orderBy: 'lastStatusUpdated',
    page: 0,
    rowsPerPage: DEFAULT_PAGE_LOAD_SIZE
};
const _NEW_SEARCH = 'Select filters or enter a PT ID or PO number to search';
const acceptedRoles = [PRAP, QUALITY, SUPERUSER];

const ClaimTableManagement = () => {
    // HOOKS
    const dispatch = useDispatch();

    // LOCAL STATE
    const [rows, setRows] = useState([]);
    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);
    const [filters, setFilters] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [allowedClaimEventStatuses, setAllowedClaimEventStatuses] = useState([]);
    const [allowedClaimEventStatusIds, setAllowedClaimEventStatusIds] = useState([]);
    const [selectedStatusId, setSelectedStatusId] = useState('');
    const [isStatusRevertEditorOpen, setIsStatusRevertEditorOpen] = useState(false);
    const [revertedRow, setRevertedRow] = useState({});
    const [isStatusCannotClaimEditorOpen, setIsStatusCannotClaimEditorOpen] = useState(false);
    const [cannotClaimRow, setCannotClaimRow] = useState({});
    const [openId, setOpenId] = useState('');
    const [isSubmitCheckedEventsDisabled, setIsSubmitCheckedEventsDisabled] = useState(false);
    const [keys, setKeys] = useState({
        claimEventStatus: '0',
        claimEventType: '1',
        contract: '2'
    });
    const [errors, setErrors] = useState({});
    const [isSearchFiltersDisabled, setIsSearchFiltersDisabled] = useState(false);
    const [isDisabled, setIsDisabled] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState(_NEW_SEARCH);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const claimEventStatuses = useSelector(
        (state) => state.entities.directusService.claimEventStatuses
    );
    const claimEventTypes = useSelector((state) => state.entities.directusService.claimEventTypes);
    const contractDetails = useSelector((state) => state.entities.directusService.contractDetails);
    const claimQueues = useSelector((state) => state.entities.participantService.claimQueues);
    const claimQueuesMetaData = useSelector(
        (state) => state.entities.participantService.claimQueuesMetaData
    );
    const loadingClaimQueuesSearch = useSelector(
        (state) => state.entities.participantService.loadingClaimQueuesSearch
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        return () => cleanUp();
    }, []);

    useEffect(() => {
        setIsDisabled(!hasRole(acceptedRoles, roles));
    }, [roles]);

    useEffect(() => {
        if (claimEventStatuses?.length < 1) return;
        const newClaimEventStatuses = claimEventStatuses.filter(
            (el) => el.name === 'Verified' || el.name === 'Claimed' || el.name === 'Paid'
        );
        setAllowedClaimEventStatuses(newClaimEventStatuses);
        setAllowedClaimEventStatusIds(newClaimEventStatuses.map((el) => el.id));
    }, [claimEventStatuses]);

    useEffect(() => {
        if (!Object.keys(filters)?.length && !searchTerm) setLoadingMessage(_NEW_SEARCH);
    }, [filters, searchTerm]);

    useEffect(() => {
        if (
            (Object.keys(filters).length || searchTerm) &&
            claimQueues.length > 0 &&
            !loadingClaimQueuesSearch &&
            'number' in claimQueuesMetaData &&
            !claimQueuesMetaData.last
        )
            dispatch(searchClaimQueue(filters, claimQueuesMetaData?.number + 1));
        else if (claimQueuesMetaData?.last && claimQueues.length > 0) {
            const newRows = claimQueues.map((el) => ({
                ...el,
                selection:
                    getNameFromId(claimEventStatuses, el.statusId).toLowerCase() === 'paid'
                        ? 'Skip'
                        : 'Proceed',
                statusName: getNameFromId(claimEventStatuses, el.statusId)
            }));
            setRows(newRows);
        } else {
            rows.length && setRows([]);
            if (
                (Object.keys(filters).length &&
                    !deepEqual(filters, { statusIds: allowedClaimEventStatusIds })) ||
                searchTerm
            )
                setLoadingMessage(CLAIMS_ERROR);
            else setLoadingMessage(_NEW_SEARCH);
        }
    }, [claimQueues, claimQueuesMetaData, loadingClaimQueuesSearch]);

    useEffect(() => {
        setIsSubmitCheckedEventsDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        setIsSubmitCheckedEventsDisabled(false);
        if (successMessage === 'Claim queues have been updated') {
            const updatedFilters = configStatuses();
            Object.keys(filters).length && dispatch(searchClaimQueue(updatedFilters, 0));
            setFilters(updatedFilters);
        }
    }, [successMessage]);

    // HELPER FUNCTIONS
    const createRows = () => {
        return stableSort(rows, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <ClaimTableRow
                key={el.id}
                row={el}
                isDisabled={isDisabled}
                openId={openId}
                toggleDropdown={onToggleDropdown}
                onRowChange={onRowChange}
            />
        ));
    };

    const configStatuses = () => {
        if (
            deepEqual(filters, { statusIds: allowedClaimEventStatusIds }) ||
            !Object.keys(filters).length
        ) {
            setFilters({});
            return {};
        }
        const newFilters =
            'statusIds' in filters && filters.statusIds.length === 1
                ? filters
                : { ...filters, statusIds: allowedClaimEventStatusIds };
        setFilters(newFilters);
        return newFilters;
    };

    const clearError = (key) => {
        setErrors((prev) => ({ ...prev, [key]: { ...errors[key], error: false } }));
    };

    const loadingError = () => {
        if (claimEventStatuses?.length < 1) return CLAIM_EVENT_STATUSES_ERROR;
        if (claimEventTypes?.length < 1) return CLAIM_EVENT_TYPES_ERROR;
        if (contractDetails?.length < 1) return CONTRACT_DETAILS_ERROR;
    };

    const resetData = () => {
        rows.length && setRows([]);
        claimQueues?.length && dispatch(clearClaimQueues());
    };

    const cleanUp = () => {
        setKeys(clearKeys(keys));
        dispatch(clearClaimQueues());
    };

    // EVENT HANDLERS
    const onUpdateFilters = (key, value) => {
        if (value === filters[key]) return;
        resetData();
        if (!value) setFilters(trimEntries([key], filters));
        else setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const onUpdateStatusIds = (chosenId) => {
        chosenId ? onUpdateFilters('statusIds', [chosenId]) : onUpdateFilters('statusIds', null);
        setSelectedStatusId(chosenId);
    };

    const onSearchDateRange = (searchRange) => {
        resetData();
        if (!searchRange) {
            if ('lastStatusUpdatedFrom' in filters && 'lastStatusUpdatedTo' in filters) {
                setFilters(trimEntries(['lastStatusUpdatedFrom', 'lastStatusUpdatedTo'], filters));
            }
        } else {
            const { startDate, endDate } = formatDateRange(searchRange);
            setFilters((prev) => ({
                ...prev,
                lastStatusUpdatedFrom: startDate,
                lastStatusUpdatedTo: endDate
            }));
        }
    };

    const onRowChange = (row) => {
        setIsSubmitCheckedEventsDisabled(false);
        if (row.selection === 'Revert') {
            setIsStatusRevertEditorOpen(true);
            setRevertedRow(row);
        } else if (row.selection === 'CannotClaim') {
            setIsStatusCannotClaimEditorOpen(true);
            setCannotClaimRow(row);
        } else {
            setRows(rows.map((el) => (el.id === row.id ? row : el)));
        }
    };

    const onStatusRevertUpdated = (revertedRow) => {
        setRows(rows.map((el) => (el.id === revertedRow.id ? revertedRow : el)));
    };

    const onStatusCannotClaimUpdated = (cannotClaimRow) => {
        setRows(rows.map((el) => (el.id === cannotClaimRow.id ? cannotClaimRow : el)));
    };

    const onStatusRevertEditorChange = (isOpen) => setIsStatusRevertEditorOpen(isOpen);

    const onStatusCannotClaimEditorChange = (isOpen) => setIsStatusCannotClaimEditorOpen(isOpen);

    const onToggleDropdown = (id) => setOpenId(id);

    const onSubmitCheckedEvents = (e) => {
        e.stopPropagation();
        let proceedIds = [];
        let reverts = [];
        let unclaimables = [];
        rows.forEach((el) => {
            if (el.selection === 'Proceed') proceedIds = [...proceedIds, el.id];
            else if (el.selection === 'Revert')
                reverts = [...reverts, { id: el.id, notes: el.notes }];
            else if (el.selection === 'CannotClaim')
                unclaimables = [
                    ...unclaimables,
                    {
                        id: el.id,
                        statusId: el.cannotClaimStatusId,
                        notes: el.notes
                    }
                ];
        });
        if (proceedIds.length > 0 || reverts.length > 0 || unclaimables.length > 0) {
            setIsSubmitCheckedEventsDisabled(true);
            dispatch(
                updateClaimQueueProgress({
                    proceedIds,
                    reverts,
                    unclaimables
                })
            );
        } else {
            setErrors({ button: { error: true, message: NO_UPDATE_ERROR } });
        }
    };

    const onSearchTermChange = (term) => {
        resetData();
        setIsSearchFiltersDisabled(!!term);
        setSearchTerm(term);
    };

    const onSearchParticipant = () => {
        if (searchTerm)
            dispatch(
                searchClaimQueue(
                    {
                        code: searchTerm,
                        statusIds: allowedClaimEventStatusIds
                    },
                    0
                )
            );
    };

    const onSubmitFilterSearch = (e) => {
        e.preventDefault();
        const updatedFilters = configStatuses();
        const newErrors = validateFilters(updatedFilters);
        if (Object.values(newErrors).some((el) => el.error)) {
            setErrors(newErrors);
            return;
        }
        dispatch(searchClaimQueue(updatedFilters, 0));
    };

    // AWAITING CONTENT
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    // RENDER
    return (
        <div className={app.pageContainer}>
            <div className={shared.headerBar}>
                <div className={app.sectionHeading}>Claim Queue</div>
            </div>
            <form onSubmit={onSubmitFilterSearch} data-testid="form_start_claim_table_management">
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <SingleSelect
                            id={'eventType'}
                            key={keys.claimEventType}
                            label={'Event Type'}
                            placeholder="Select event type..."
                            disabled={isDisabled || isSearchFiltersDisabled}
                            menuItems={claimEventTypes || []}
                            selectedId={filters?.eventTypeId || ''}
                            selected={
                                claimEventTypes.find((el) => el.id === filters?.eventTypeId) || {}
                            }
                            onChange={(chosenId) => onUpdateFilters('eventTypeId', chosenId)}
                        />

                        <DateRangePicker
                            id="lastStatusUpdated"
                            label="Last Updated"
                            disabled={isDisabled || isSearchFiltersDisabled}
                            onSearch={onSearchDateRange}
                        />
                    </div>
                    <div className={form.formColumn}>
                        <div>
                            <SingleSelect
                                id={'contractId'}
                                key={keys.contract}
                                label={'Contract'}
                                placeholder="Select contract..."
                                disabled={isDisabled || isSearchFiltersDisabled}
                                menuItems={contractDetails || []}
                                selectedId={filters?.contractId || ''}
                                selected={
                                    contractDetails.find((el) => el.id === filters?.contractId) ||
                                    {}
                                }
                                onChange={(chosenId) => onUpdateFilters('contractId', chosenId)}
                            />
                            <SingleSelect
                                id={'statusIds'}
                                key={keys.claimEventStatus}
                                label={'Status'}
                                placeholder="Select status..."
                                disabled={isDisabled || isSearchFiltersDisabled}
                                menuItems={allowedClaimEventStatuses || []}
                                selectedId={selectedStatusId || ''}
                                selected={
                                    allowedClaimEventStatuses.find(
                                        (el) => el.id === selectedStatusId
                                    ) || {}
                                }
                                onChange={(chosenId) => onUpdateStatusIds(chosenId)}
                            />
                        </div>
                    </div>
                </div>
                <Button
                    id="claimClaimQueueSearch"
                    content="Search"
                    disabled={isDisabled || isSearchFiltersDisabled}
                    error={errors.button}
                    clearError={() => clearError('button')}
                />
            </form>
            <div className={shared.claimsToolBarWrapper}>
                <div className={shared.claimsToolBar} data-testid="search_field">
                    <label className={form.formLabel}>Search PT ID & PO Number</label>
                    <SearchOnEnter
                        searchTerm={searchTerm}
                        placeholder="Search ID and PO..."
                        autocomplete="off"
                        disabled={isDisabled}
                        search={onSearchParticipant}
                        onChange={onSearchTermChange}
                    />
                </div>
                <StatusRevertEditor
                    isOpen={isStatusRevertEditorOpen}
                    onStatusRevertEditorChange={onStatusRevertEditorChange}
                    revertedRow={revertedRow}
                    onStatusRevertUpdated={onStatusRevertUpdated}
                />
                <StatusCannotClaimEditor
                    isOpen={isStatusCannotClaimEditorOpen}
                    onStatusCannotClaimEditorChange={onStatusCannotClaimEditorChange}
                    cannotClaimRow={cannotClaimRow}
                    onStatusCannotClaimUpdated={onStatusCannotClaimUpdated}
                    claimEventStatuses={claimEventStatuses}
                />
            </div>
            {rows?.length < 1 ? (
                loadingClaimQueuesSearch ? (
                    <LoadingSpinner />
                ) : (
                    <div>{loadingMessage}</div>
                )
            ) : (
                <div>
                    <ResultsTable
                        defaultOrderBy={rowMetaData.orderBy}
                        headCells={headCells}
                        passRowMetaDataUp={setRowMetaData}
                        tableRows={createRows()}
                        loadResults={searchClaimQueue}
                        apiParams={filters}
                        totalResults={claimQueuesMetaData.totalElements}
                        metaData={claimQueuesMetaData}
                        loadingValues={loadingClaimQueuesSearch}
                    />
                    <Button
                        id="submitButtonClaimQueues"
                        type="button"
                        content="Submit Checked Events"
                        disabled={isDisabled || isSubmitCheckedEventsDisabled}
                        error={errors.button}
                        clearError={() => clearError('button')}
                        onClick={onSubmitCheckedEvents}
                    />
                </div>
            )}
        </div>
    );
};

export default ClaimTableManagement;
